import { useNavigate, useParams } from 'react-router-dom';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetAvatarSeriesQuery, type AvatarItem } from '@/services/avatarApi';
import { AvatarSkeleton } from '@/components/avatars/AvatarSkeleton';

export function AvatarSeries() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('avatars');

  const { data: items = [], isLoading } = useGetAvatarSeriesQuery(id, { skip: !id });

  const onCardClick = useCallback(
    (avatar: AvatarItem) => navigate(`/avatars/${avatar._id}`, { replace: true }),
    [navigate],
  );

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header>
        {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. Il conteggio qui sotto e' un DATO e resta. */}
        <h1 className="sr-only">{t('series.title')}</h1>
        <p className="text-sm text-fg-muted">
          {isLoading
            ? t('series.loading')
            : t('series.summary', { count: items.length })}
        </p>
      </header>

      {isLoading ? (
        <AvatarSkeleton />
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {t('series.empty')}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {items.map((avatar) => (
            <SeriesCardWrapper key={avatar._id} avatar={avatar} onClick={onCardClick} />
          ))}
        </div>
      )}
    </div>
  );
}

function SeriesCardWrapper({
  avatar,
  onClick,
}: {
  avatar: AvatarItem;
  onClick: (avatar: AvatarItem) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(avatar)}
      className="flex flex-col overflow-hidden rounded-xl bg-bg-secondary text-left transition-opacity active:opacity-80"
    >
      <div className="aspect-square overflow-hidden border border-black/40 bg-black">
        {avatar.thumbnailUrl && (
          <img
            src={avatar.thumbnailUrl}
            alt={avatar.title}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        )}
      </div>
      <div className="p-2">
        <p className="truncate text-[13px] font-semibold text-fg">{avatar.title}</p>
      </div>
    </button>
  );
}
